import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { log } from '@/lib/logger';

/**
 * Revalida periodicamente o JWT contra o Auth server (re-hit em
 * `getUser`) e refaz fetch de roles/status. Se o usuário tiver sido
 * suspenso, rebaixado ou tiver o token revogado, derruba a sessão.
 *
 * Frequência padrão: 5 min. Princípio de menor privilégio: a UI
 * nunca renderiza ações que o backend não autorizaria mais.
 */
export function useSessionRevalidate(opts: { minutes?: number } = {}) {
  const minutes = opts.minutes ?? 5;
  const qc = useQueryClient();

  useEffect(() => {
    const tick = async () => {
      const { data: sessionRes } = await supabase.auth.getSession();
      if (!sessionRes.session) return;
      const { data, error } = await supabase.auth.getUser();
      if (error || !data?.user) {
        log.warn({ err: error }, 'auth.revalidate.invalid');
        try {
          await supabase.auth.signOut({ scope: 'global' });
        } catch (err) {
          log.warn({ err }, 'auth.revalidate.signout_failed');
        }
        return;
      }
      // Força refetch de roles/profile-status sem invalidar todo o cache.
      qc.invalidateQueries({ queryKey: ['userRoles'] });
      qc.invalidateQueries({ queryKey: ['userProfile'] });
      qc.invalidateQueries({ queryKey: ['billingStatus'] });
    };

    const id = setInterval(tick, minutes * 60_000);
    const onFocus = () => tick();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [minutes, qc]);
}
